import type { PropsWithChildren, ReactNode } from 'react';
import React from 'react';

import { Card } from 'antd';
import { Navigate } from 'react-router';

import { pipe } from '@code-expert/prelude';
import type { GroupOption } from '/imports/api/hooks/client/courseSelect';
import type { CourseUrl, GroupId, SemesterId } from '/imports/domain';
import { concatRoutes, formatRoute, parser } from '/imports/modules/Routing';
import { Authorise } from '/imports/ui/components/Authorise';
import { MainContent } from '/imports/ui/components/MainContent';
import { useSetPreferredEntry } from '/imports/ui/hooks/useSetPreferredEntry';
import { TopNavLayout } from '/imports/ui/layouts/TopNavLayout';
import { setPageParamsAdt } from '/imports/ui/navigation/pageParams';
import { setPageParamsUiState } from '/imports/ui/navigation/pageParamsUiState';
import type { Page } from './pages';
import {
  detailsRoute,
  groupRoute,
  myGroupsRoute,
  participationRoute,
  reviewExercisesLink,
  reviewExercisesRoute,
  reviewPresentationsRoute,
  schedulePresentationsRoute,
  studentsRoute,
} from './routes';

const Navigation = React.lazy(() => import('./Navigation'));

const Entry = React.lazy(() => import('./Entry'));

const GroupDetails = React.lazy(() => import('/imports/ui/pages/myGroups/details/GroupDetails'));

const GroupReview = React.lazy(() => import('./details/GroupReview'));

const ChartParticipation = React.lazy(
  () => import('/imports/ui/components/charts/ChartParticipation'),
);

const ReviewPresentations = React.lazy(() => import('./Appointments/ReviewPresentations'));

const SchedulePresentations = React.lazy(() => import('./Appointments/SchedulePresentations'));

const StudentOverview = React.lazy(
  () => import('/imports/ui/pages/myCourses/studentOverviewTable/StudentOverview'),
);

const MyGroupsWrapper = ({ children }: PropsWithChildren) => (
  <Authorise
    allow={['groupAdmin']}
    deny={['lti']}
    layout={(children) => <TopNavLayout selected="mygroups">{children}</TopNavLayout>}
  >
    {children}
  </Authorise>
);

const MyGroupsPage = ({
  page,
  semester,
  courseUrl,
  groupId,
  children,
}: {
  page: Page;
  semester: SemesterId;
  courseUrl: CourseUrl;
  groupId: GroupId;
  children: ReactNode | ((group: GroupOption) => ReactNode);
}) => {
  useSetPreferredEntry({ path: formatRoute(myGroupsRoute)({}), semester, courseUrl, groupId });
  setPageParamsUiState(setPageParamsAdt.mygroups({ semester, courseUrl, groupId, page }));
  return (
    <MyGroupsWrapper>
      <Navigation page={page} semester={semester} courseUrl={courseUrl} groupId={groupId}>
        {children}
      </Navigation>
    </MyGroupsWrapper>
  );
};

export const myGroupsRouter = concatRoutes([
  pipe(
    reviewExercisesRoute.parser,
    parser.map(({ semester, courseUrl, groupId, section }) => (
      <MyGroupsPage
        page="reviewExercises"
        semester={semester}
        courseUrl={courseUrl}
        groupId={groupId}
      >
        <GroupReview
          groupId={groupId}
          courseUrl={courseUrl}
          semester={semester}
          section={section}
        />
      </MyGroupsPage>
    )),
  ),
  pipe(
    studentsRoute.parser,
    parser.map(({ semester, courseUrl, groupId }) => (
      <MyGroupsPage page="students" semester={semester} courseUrl={courseUrl} groupId={groupId}>
        {({ courseId }) => (
          <MainContent title="Student overview">
            <StudentOverview
              courseId={courseId}
              courseUrl={courseUrl}
              semester={semester}
              groupId={groupId}
            />
          </MainContent>
        )}
      </MyGroupsPage>
    )),
  ),
  pipe(
    participationRoute.parser,
    parser.map(({ semester, courseUrl, groupId }) => (
      <MyGroupsPage
        page="participation"
        semester={semester}
        courseUrl={courseUrl}
        groupId={groupId}
      >
        <MainContent title="Participation">
          <Card variant="borderless">
            <ChartParticipation groupId={groupId} height={350} />
          </Card>
        </MainContent>
      </MyGroupsPage>
    )),
  ),
  pipe(
    reviewPresentationsRoute.parser,
    parser.map(({ semester, courseUrl, groupId, section }) => (
      <MyGroupsPage
        page="reviewPresentations"
        semester={semester}
        courseUrl={courseUrl}
        groupId={groupId}
      >
        {({ courseId }) => (
          <ReviewPresentations
            courseId={courseId}
            semester={semester}
            section={section}
            sectionLink={(section) =>
              formatRoute(reviewPresentationsRoute)({ semester, courseUrl, groupId, section })
            }
          />
        )}
      </MyGroupsPage>
    )),
  ),
  pipe(
    schedulePresentationsRoute.parser,
    parser.map(({ semester, courseUrl, groupId }) => (
      <MyGroupsPage
        page="schedulePresentations"
        semester={semester}
        courseUrl={courseUrl}
        groupId={groupId}
      >
        {({ courseId }) => <SchedulePresentations semester={semester} courseId={courseId} />}
      </MyGroupsPage>
    )),
  ),
  pipe(
    detailsRoute.parser,
    parser.map(({ semester, courseUrl, groupId }) => (
      <MyGroupsPage page="details" semester={semester} courseUrl={courseUrl} groupId={groupId}>
        <GroupDetails groupId={groupId} />
      </MyGroupsPage>
    )),
  ),

  pipe(
    groupRoute.parser,
    parser.map(({ semester, courseUrl, groupId }) => (
      <MyGroupsWrapper>
        <Navigate to={reviewExercisesLink({ semester, courseUrl, groupId })} />
      </MyGroupsWrapper>
    )),
  ),
  pipe(
    myGroupsRoute.parser,
    parser.map(() => (
      <MyGroupsWrapper>
        <Entry />
      </MyGroupsWrapper>
    )),
  ),
]);
